/**
 * Temps de trajet ("transport") entre deux shifts d'un même collaborateur sur une même
 * journée, dans deux boutiques différentes.
 *
 * Règle métier :
 * - Coupure courte entre les 2 shifts (`isPaidTransportGap`) => temps de trajet PAYÉ, divisé en
 *   deux moitiés ventilées sur les 2 boutiques (la 1re moitié imputée à la boutique de départ,
 *   la 2nde à la boutique d'arrivée).
 * - Coupure plus longue => coupure, temps NON payé (aucun segment).
 *
 * Modèle dérivé : aucun stockage en base. Les segments sont une fonction pure des deux shifts
 * voisins. Ce helper est agnostique du type de magasin — l'appelant doit pré-filtrer les
 * shifts physiques (storeType !== 'REMOTE') avant de l'appeler.
 */

/**
 * Journée (YYYY-MM-DD, Europe/Paris) à partir de laquelle la coupure payée en trajet passe de
 * « < 45 min » à « ≤ 55 min » (demande RH, kanban #1099). Les journées antérieures gardent
 * l'ancienne règle : les exports de paie déjà sortis restent identiques.
 */
export const PAID_TRANSPORT_GAP_RULE_CHANGE_DAY = '2026-11-01'
// Jusqu'au 31/10/2026 : coupure strictement inférieure à 45 min.
const PAID_TRANSPORT_GAP_LIMIT_MINUTES_BEFORE = 45
// À partir du 01/11/2026 : coupure de 55 min au plus (55 min pile est payée).
const PAID_TRANSPORT_GAP_MAX_MINUTES_FROM_CHANGE = 55
// Plus petite coupure qui dérive des segments de transport : en dessous, une des deux moitiés
// serait de durée nulle (cas dégénéré, hors grille 5 min).
const MIN_TRANSPORT_SEGMENT_GAP_MINUTES = 2

/**
 * La journée `day` (YYYY-MM-DD, Europe/Paris) tombe-t-elle le 01/11/2026 ou après ? Seul aiguillage
 * par date, partagé par le prédicat et par son libellé, et réutilisé par la franchise d'entrée du
 * pointage retenu (backend, `retainedTimes`), qui bascule le même jour (kanban #1099).
 */
export const isOnOrAfterPaidTransportGapRuleChangeDay = (day: string): boolean =>
  day >= PAID_TRANSPORT_GAP_RULE_CHANGE_DAY

/**
 * Seule source de vérité de la borne haute du trajet payé : une coupure de `gapMinutes` entre deux
 * shifts de boutiques différentes, sur la journée de travail `day` (YYYY-MM-DD, Europe/Paris, celle
 * sous laquelle l'appelant groupe ses créneaux), est-elle du temps de trajet payé ? La borne basse
 * dépend de l'usage : `hasPaidTransportSegment` (segments de transport) et
 * `isPaidTransportMerge` (fusion des exports).
 */
export const isPaidTransportGap = (gapMinutes: number, day: string): boolean =>
  isOnOrAfterPaidTransportGapRuleChangeDay(day)
    ? gapMinutes <= PAID_TRANSPORT_GAP_MAX_MINUTES_FROM_CHANGE
    : gapMinutes < PAID_TRANSPORT_GAP_LIMIT_MINUTES_BEFORE

/** Règle de la journée, à afficher à l'utilisateur : « < 45 min » ou « ≤ 55 min ». */
export const paidTransportGapRuleLabel = (day: string): string =>
  isOnOrAfterPaidTransportGapRuleChangeDay(day)
    ? `≤ ${PAID_TRANSPORT_GAP_MAX_MINUTES_FROM_CHANGE} min`
    : `< ${PAID_TRANSPORT_GAP_LIMIT_MINUTES_BEFORE} min`

/**
 * Une coupure de `gapMinutes` entre deux shifts de boutiques différentes, sur la journée `day`,
 * dérive-t-elle des segments de transport payé ? Coupure d'au moins 2 min (en dessous, une des deux
 * moitiés serait nulle) et courte au sens de `isPaidTransportGap`. Partagé par les segments
 * (`computeDayTransportSegments`), les minutes de trajet des exports planning et l'annonce
 * `transportGenerated` de la création multi-boutiques, qui doivent rester alignés. La comparaison
 * des boutiques reste à l'appelant.
 */
export const hasPaidTransportSegment = (gapMinutes: number, day: string): boolean =>
  gapMinutes >= MIN_TRANSPORT_SEGMENT_GAP_MINUTES && isPaidTransportGap(gapMinutes, day)

/**
 * Fusion « trajet payé » des exports (pointages V2 et legacy, planning courant et legacy) : deux
 * créneaux consécutifs d'une même journée `day`, séparés d'une coupure de `gapMinutes` (null si une
 * borne manque), forment un seul bloc entrée/sortie s'ils sont dans deux boutiques différentes et
 * que la coupure est positive ou nulle et courte au sens de `isPaidTransportGap`. Les conditions
 * propres à chaque export (adjacence des créneaux, statut de validation…) restent à l'appelant.
 */
export const isPaidTransportMerge = ({
  gapMinutes,
  day,
  previousStoreId,
  nextStoreId,
}: {
  gapMinutes: number | null
  day: string
  previousStoreId: number
  nextStoreId: number
}): boolean =>
  gapMinutes !== null &&
  gapMinutes >= 0 &&
  isPaidTransportGap(gapMinutes, day) &&
  previousStoreId !== nextStoreId

export interface TransportInputShift {
  collaboratorId: number
  date: string // YYYY-MM-DD
  storeId: number
  startTime: string // HH:mm
  endTime: string // HH:mm
}

export interface TransportSegment {
  collaboratorId: number
  date: string
  startTime: string // HH:mm (borne de la moitié)
  endTime: string // HH:mm
  imputedStoreId: number // boutique qui paie cette moitié
  fromStoreId: number // boutique du shift précédent
  toStoreId: number // boutique du shift suivant
}

const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/

const parseTimeToMinutes = (time: string): number | null => {
  const match = TIME_REGEX.exec(time.trim())
  if (!match) return null
  return Number(match[1]) * 60 + Number(match[2])
}

const minutesToTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

interface NormalizedShift extends TransportInputShift {
  startMinutes: number
  endMinutes: number
}

/**
 * Groupe les shifts par (collaborateur, jour), les trie, et renvoie pour chaque coupure
 * éligible la fonction de calcul. Ignore les shifts au format invalide ou de nuit
 * (endMinutes <= startMinutes).
 */
const groupAndNormalize = (shifts: TransportInputShift[]): Map<string, NormalizedShift[]> => {
  const groups = new Map<string, NormalizedShift[]>()
  for (const shift of shifts) {
    const startMinutes = parseTimeToMinutes(shift.startTime)
    const endMinutes = parseTimeToMinutes(shift.endTime)
    if (startMinutes === null || endMinutes === null) continue
    if (endMinutes <= startMinutes) continue // shift de nuit / malformé : hors périmètre

    const key = `${shift.collaboratorId}__${shift.date}`
    const group = groups.get(key) ?? []
    group.push({ ...shift, startMinutes, endMinutes })
    groups.set(key, group)
  }

  for (const group of groups.values()) {
    group.sort((a, b) => a.startMinutes - b.startMinutes)
  }

  return groups
}

/**
 * Pour chaque paire de shifts adjacents (boutiques différentes, coupure retenue par
 * `hasPaidTransportSegment` sur la journée des shifts), renvoie deux segments de transport
 * (première / deuxième moitié de la coupure).
 *
 * Le point milieu est arrondi à la minute (`Math.round(gap / 2)`) : pour une coupure de
 * durée impaire, la 1re moitié peut être 1 min plus longue que la 2nde. Le coût doit utiliser
 * les minutes réelles de chaque segment.
 */
export const computeDayTransportSegments = (shifts: TransportInputShift[]): TransportSegment[] => {
  const groups = groupAndNormalize(shifts)
  const segments: TransportSegment[] = []

  for (const group of groups.values()) {
    for (let i = 0; i < group.length - 1; i++) {
      const current = group[i]
      const next = group[i + 1]
      const gap = next.startMinutes - current.endMinutes

      if (!hasPaidTransportSegment(gap, current.date)) continue
      if (current.storeId === next.storeId) continue

      const midMinutes = current.endMinutes + Math.round(gap / 2)

      segments.push({
        collaboratorId: current.collaboratorId,
        date: current.date,
        startTime: minutesToTime(current.endMinutes),
        endTime: minutesToTime(midMinutes),
        imputedStoreId: current.storeId,
        fromStoreId: current.storeId,
        toStoreId: next.storeId,
      })
      segments.push({
        collaboratorId: current.collaboratorId,
        date: current.date,
        startTime: minutesToTime(midMinutes),
        endTime: minutesToTime(next.startMinutes),
        imputedStoreId: next.storeId,
        fromStoreId: current.storeId,
        toStoreId: next.storeId,
      })
    }
  }

  return segments
}
